import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUpload } from "../lib/UploadContext";

function useElapsed(startTime) {
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startTime) return "";
  const s = Math.floor((Date.now() - startTime) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function BatchRow({ batch }) {
  const elapsed = useElapsed(batch.status !== "COMPLETE" ? batch.startTime : null);
  const isDone  = batch.status === "COMPLETE";
  const files   = batch.files || [];

  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      {/* Batch header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#e2e8f0", fontFamily: "var(--font-mono)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
          {batch.name}
        </span>
        <span style={{ fontSize: 11, color: isDone ? "#4ade80" : "#94a3b8", fontFamily: "var(--font-mono)", flexShrink: 0, marginLeft: 8 }}>
          {isDone ? "Done" : `${batch.completed}/${batch.total} · ${elapsed}`}
        </span>
      </div>

      {/* Individual file rows */}
      {files.map(f => {
        const isPassed     = f.status === "PASSED";
        const isFailed     = f.status === "FAILED";
        const isProcessing = f.status === "PROCESSING";
        const color        = (isPassed || isFailed) ? "#4ade80" : "#94a3b8";
        const elapsed = (isPassed || isFailed) && f.completed_at && batch.startTime
          ? (() => {
              const diff = Math.floor((new Date(f.completed_at) - new Date(batch.startTime)) / 1000);
              return diff < 60 ? `${diff}s` : `${Math.floor(diff/60)}m ${diff%60}s`;
            })()
          : null;
        const label = (isPassed || isFailed) ? `Uploaded${elapsed ? ` · ${elapsed}` : ""}` : "Processing…";
        const shortName    = f.filename.length > 30 ? f.filename.slice(0, 30) + "…" : f.filename;
        return (
          <div key={f.filename} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0 3px 10px" }}>
            <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 }}>
              {isProcessing && (
                <svg width="9" height="9" viewBox="0 0 16 16" fill="none" stroke="#6366f1" strokeWidth="2" style={{ marginRight: 4, verticalAlign: "middle", animation: "upload-spin 1.2s linear infinite" }}>
                  <path d="M8 2a6 6 0 1 0 6 6" strokeLinecap="round"/>
                </svg>
              )}
              {isPassed && <span style={{ color: "#4ade80", marginRight: 4 }}>✓</span>}
              {isFailed && <span style={{ color: "#4ade80", marginRight: 4 }}>✓</span>}
              {shortName}
            </span>
            <span style={{ fontSize: 10, color, fontFamily: "var(--font-mono)", flexShrink: 0, marginLeft: 6 }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SingleRow({ single }) {
  const isDone  = single.status === "COMPLETE";
  const isError = single.status === "ERROR";
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 11, color: "#e2e8f0", fontFamily: "var(--font-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 190 }}>
          {single.filename.length > 28 ? single.filename.slice(0, 28) + "…" : single.filename}
        </span>
        <span style={{ fontSize: 11, flexShrink: 0, marginLeft: 8, fontFamily: "var(--font-mono)",
          color: isDone ? "#4ade80" : isError ? "#f87171" : "#94a3b8" }}>
          {isDone ? "Done" : isError ? "Error" : `${single.pct}%`}
        </span>
      </div>
      <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${single.pct}%`, height: "100%", borderRadius: 2, transition: "width 0.6s ease",
          background: isDone ? "#4ade80" : isError ? "#f87171" : "#6366f1" }} />
      </div>
    </div>
  );
}

export default function UploadStatusBar() {
  const { batches, singles, activeCount, totalDocs, completedDocs, clearCompleted } = useUpload();
  const [open, setOpen] = useState(false);
  const navigate   = useNavigate();
  const location   = useLocation();

  const allItems = [...batches, ...singles];
  if (!allItems.length) return null;
  if (location.pathname === "/upload") return null;

  const activeBatches = batches.filter(b => b.status !== "COMPLETE");
  const activeSingles = singles.filter(s => s.status === "PROCESSING");
  const allDone       = activeCount === 0;
  const overallPct    = totalDocs > 0 ? Math.round((completedDocs / totalDocs) * 100) : 0;
  const hasError      = singles.some(s => s.status === "ERROR");
  const iconColor     = hasError ? "#f87171" : allDone ? "#4ade80" : "#6366f1";

  return (
    <div style={{ position: "fixed", bottom: 24, right: 84, zIndex: 900, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>

      {/* Expanded panel */}
      {open && (
        <div style={{
          width: 320,
          background: "#1e293b",
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          border: "1px solid rgba(255,255,255,0.1)",
          overflow: "hidden",
        }}>
          <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: "#f1f5f9", fontWeight: 600 }}>
                {allDone ? "Processing complete" : `Processing ${activeBatches.length + activeSingles.length > 1
                  ? `${activeBatches.length + activeSingles.length} uploads`
                  : activeBatches[0]?.name || activeSingles[0]?.filename || "documents"}`}
              </span>
              {!allDone && (
                <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "#94a3b8" }}>
                  {completedDocs}/{totalDocs}
                </span>
              )}
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ width: `${allDone ? 100 : overallPct}%`, height: "100%", background: iconColor, borderRadius: 2, transition: "width 0.5s ease" }} />
            </div>
          </div>

          <div style={{ padding: "4px 14px 2px", maxHeight: 240, overflowY: "auto" }}>
            {batches.map(b => <BatchRow key={b.batch_id} batch={b} />)}
            {singles.map(s => <SingleRow key={s.filename} single={s} />)}
          </div>

          <div style={{ display: "flex", gap: 8, padding: "8px 14px 12px" }}>
            <button
              onClick={() => navigate("/upload")}
              style={{ fontSize: 11, background: "rgba(99,102,241,0.2)", color: "#a5b4fc", border: "none",
                borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontFamily: "var(--font-mono)" }}>
              Upload Page
            </button>
            {allDone && (
              <button
                onClick={() => { clearCompleted(); setOpen(false); }}
                style={{ fontSize: 11, background: "none", color: "#64748b", border: "none",
                  cursor: "pointer", padding: "5px 10px", fontFamily: "var(--font-mono)" }}>
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {/* Floating icon button */}
      <button
        onClick={() => setOpen(o => !o)}
        title={allDone ? "Processing complete" : `Processing ${totalDocs - completedDocs} document(s)…`}
        style={{
          width: 48, height: 48,
          borderRadius: "50%",
          border: "none",
          background: "#1e293b",
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative",
          outline: open ? `2px solid ${iconColor}` : "none",
          outlineOffset: 2,
          transition: "outline 0.2s",
        }}
      >
        {allDone ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ animation: "upload-spin 1.2s linear infinite" }}>
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
        )}
        {!allDone && (
          <span style={{
            position: "absolute", top: -3, right: -3,
            background: "#6366f1", color: "#fff",
            fontSize: 9, fontWeight: 700,
            borderRadius: "50%", width: 16, height: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--font-mono)",
            border: "2px solid #fff",
          }}>
            {totalDocs - completedDocs}
          </span>
        )}
      </button>

      <style>{`@keyframes upload-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}